// Low-level Slack channel senders for the manager.
//
// Depends only on the shared channel state (./state.ts), the runtime log leaf,
// and the pure delivery-hint gate — no other manager module — so it sits in
// the channel leaf layer importable by the delivery orchestration in
// manager-worker.ts without a cycle. The cron-alert formatter `sendSlackAlert`
// deliberately stays in manager-worker.ts because it is coupled to the
// cron-monitor `CronAlert` type; it calls `sendSlackWebhookMessage` from here.
// See ADR-0011.

import { log } from '../runtime.js';
import { hintProbability, pickHintVariant, shouldIncludeHint } from '../../delivery-hint.js';
import { agentChannelTokens, getAlertSlackWebhook } from './state.js';

export async function sendSlackWebhookMessage(text: string): Promise<void> {
  const alertSlackWebhook = getAlertSlackWebhook();
  if (!alertSlackWebhook) {
    log('sendSlackWebhookMessage: no alertSlackWebhook configured — message dropped');
    return;
  }
  try {
    // 5s abort matches postSlackChannelMessage / conversations.open — a stalled
    // network path must not block this awaited delivery indefinitely (CR #2145).
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5_000);
    try {
      const response = await fetch(alertSlackWebhook, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
        signal: controller.signal,
      });
      if (!response.ok) {
        log(`Slack webhook failed: ${response.status} ${response.statusText}`);
      }
    } finally {
      clearTimeout(timeout);
    }
  } catch (err) {
    log(`Slack webhook error: ${(err as Error).message}`);
  }
}

/**
 * Post a message to a specific Slack channel using the agent's bot token
 * cached from channel_configs during refresh. Returns true if sent successfully.
 */
export async function sendSlackChannelMessage(agentCodeName: string, channelId: string, text: string): Promise<boolean> {
  const result = await postSlackChannelMessage(agentCodeName, channelId, text);
  return result.ok;
}

/**
 * Like sendSlackChannelMessage, but returns the posted message's ts so the
 * caller can thread a follow-up reply under it. Accepts an optional
 * `threadTs` to post *as* a thread reply. ENG-4457 needs this to thread
 * the periodic discoverability hint under the primary scheduled delivery.
 */
export async function postSlackChannelMessage(
  agentCodeName: string,
  channelId: string,
  text: string,
  threadTs?: string,
): Promise<{ ok: boolean; ts?: string; error?: string }> {
  const botToken = agentChannelTokens.get(agentCodeName)?.slack;
  if (!botToken) {
    log(`No Slack bot token cached for '${agentCodeName}' — cannot post to ${channelId}`);
    return { ok: false };
  }

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5_000);
    try {
      const body: Record<string, unknown> = { channel: channelId, text };
      if (threadTs) body.thread_ts = threadTs;
      const response = await fetch('https://slack.com/api/chat.postMessage', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${botToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      const data = await response.json() as { ok: boolean; error?: string; ts?: string };
      if (!data.ok) {
        log(`Slack chat.postMessage failed for '${agentCodeName}' to ${channelId}: ${data.error}`);
        // Surface Slack's error code so callers can branch (ENG-6038 needs
        // thread_not_found / message_not_found for the top-level fallback).
        return { ok: false, error: data.error };
      }
      return { ok: true, ts: data.ts };
    } finally {
      clearTimeout(timeout);
    }
  } catch (err) {
    log(`Slack channel message error for '${agentCodeName}': ${(err as Error).message}`);
    return { ok: false };
  }
}

// ENG-4457 — periodic discoverability hint. Rolled on each successful
// scheduled delivery; on a hit, post one follow-up message (Slack as a
// thread reply, Telegram as a second sendMessage) pointing the user at
// "you can change this schedule conversationally". The core gating lives
// in delivery-hint.ts so the decision is unit-testable.

export async function maybePostSlackThreadHint(
  agentCodeName: string,
  channelId: string,
  primaryTs: string | undefined,
): Promise<void> {
  if (!shouldIncludeHint(hintProbability(agentCodeName))) return;
  if (!primaryTs) {
    // No ts means we can't thread. Skip rather than post a loose second
    // message in the channel — that would look like noise.
    return;
  }
  const hint = pickHintVariant();
  const result = await postSlackChannelMessage(agentCodeName, channelId, hint, primaryTs);
  if (result.ok) {
    log(`[delivery-hint] Slack thread hint posted for '${agentCodeName}'`);
  }
}
